import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConfirmModal, FullScreenLoading } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getActiveLubavSession,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  clearCompareDraft,
  type CompareDraft,
  selectCompareDraft,
  setCompareDraft,
} from '@backrs_ui/grow-ui/src/redux/userColleges/compareDraft.slice';
import {
  useCreateComparisonMutation,
  useDeleteComparisonMutation,
  useGetComparisonDefaultsQuery,
  useGetComparisonQuery,
  useScoreComparisonQuery,
  useUpdateComparisonMutation,
  userCollegesApi,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.api';
import type {
  CollegePick,
  CompareFactor,
  ComparisonSettings,
  ComparisonSummary,
  UserCollege,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { orderedByMatch } from './compare.util';
import { useAgentContext } from './ComparePage.agentContext';
import { BeforeYouBegin } from './components/BeforeYouBegin';
import { CompareAskLubav } from './components/CompareAskLubav';
import { CompareCards } from './components/CompareCards';
import { CompareHeader } from './components/CompareHeader';
import { CompareHeatMap } from './components/CompareHeatMap';
import { ComparisonControls, CompareView } from './components/ComparisonControls';
import { FitLegend } from './components/FitLegend';
import { CompareWeights } from './components/WeightsModals';

const STRINGS = appStrings.modules.myColleges.compare;
const COMPARISON_PARAM = 'comparison';
const samePicks = (a: readonly CollegePick[], b: readonly CollegePick[]) =>
  a.length === b.length &&
  a.every((pick, index) => pick.institution_id === b[index].institution_id);

type ComparePageProps = Readonly<{
  colleges: readonly UserCollege[];
  comparisons: readonly ComparisonSummary[];
}>;

export const ComparePage = ({ colleges, comparisons }: ComparePageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<CompareView>('heatMap');
  const dispatch = useAppDispatch();
  const draft = useAppSelector(selectCompareDraft);
  const [edits, setEdits] = useState<CompareDraft | null>(null);
  const [staged, setStaged] = useState<readonly CollegePick[] | null>(null);
  const [weightsFor, setWeightsFor] = useState<CompareFactor | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<ComparisonSummary | null>(null);
  const isPhone = useMediaQuery(({ breakpoints }: Theme) => breakpoints.down('md'));
  const [createComparison, { isLoading: isCreating, isError: createFailed, reset: resetCreate }] =
    useCreateComparisonMutation();
  const [updateComparison, { isLoading: isUpdating, isError: updateFailed, reset: resetUpdate }] =
    useUpdateComparisonMutation();
  const [deleteComparison, { isError: deleteFailed, reset: resetDelete }] =
    useDeleteComparisonMutation();

  const requestedId = Number(searchParams.get(COMPARISON_PARAM));
  const savedId = comparisons.find((summary) => summary.id === requestedId)?.id;
  const savedQuery = useGetComparisonQuery(savedId ?? skipToken);
  const saved = savedId === undefined ? undefined : savedQuery.data;

  const working: CompareDraft | undefined =
    savedId === undefined
      ? draft
      : (edits ?? (saved && { picks: saved.colleges, settings: saved.settings }));
  const picks = working?.picks ?? [];
  const scoresLocally = savedId === undefined || edits !== null;
  const scoreQuery = useScoreComparisonQuery(
    scoresLocally
      ? {
          institution_ids: picks.map((pick) => pick.institution_id),
          settings: working?.settings,
        }
      : skipToken,
  );
  const scored = scoresLocally ? scoreQuery.data : saved;
  const { isLoading, isError } = scoresLocally ? scoreQuery : savedQuery;
  const comparison = scored && { ...scored, colleges: orderedByMatch(scored.colleges) };
  const shown = staged ?? picks;
  const canCompare = staged !== null && staged.length > 0 && !samePicks(staged, picks);
  const canSave = shown.length > 0 && (savedId === undefined || edits !== null || canCompare);
  const shownView: CompareView = isPhone ? 'cards' : view;
  const { data: studentWeights } = useGetComparisonDefaultsQuery(undefined);

  useAgentContext({
    comparison,
    saved,
    view: shownView,
    weightsOpen: weightsFor !== undefined,
    invitedToSetWeights: studentWeights?.saved === false,
  });
  useRefetchAfterLubavTurn();

  const setWorking = (next: CompareDraft) => {
    if (savedId === undefined) dispatch(setCompareDraft(next));
    else setEdits(next);
  };

  const showComparison = (id?: number) => {
    resetUpdate();
    resetDelete();
    setEdits(null);
    setStaged(null);
    setSearchParams(id === undefined ? {} : { [COMPARISON_PARAM]: String(id) }, { replace: true });
  };

  const startNew = () => {
    dispatch(clearCompareDraft());
    showComparison();
  };

  const handleCompare = () => {
    if (!staged || !working) return;

    setWorking({ ...working, picks: staged });
    setStaged(null);
  };

  const scoreOnStudentWeights = () => {
    if (working) setWorking({ picks: working.picks });
  };

  const handleSettingsChange = (settings: ComparisonSettings) => {
    if (working) setWorking({ ...working, settings });
  };

  const handleSave = async (name: string) => {
    resetCreate();
    const result = await createComparison({
      name,
      institution_ids: shown.map((pick) => pick.institution_id),
      settings: draft.settings,
    });

    if (!result.data) return false;

    dispatch(clearCompareDraft());
    showComparison(result.data.id);

    return true;
  };

  const handleUpdate = async () => {
    if (savedId === undefined || !working) return;

    const result = await updateComparison({
      id: savedId,
      institution_ids: shown.map((pick) => pick.institution_id),
      settings: working.settings ?? comparison?.settings,
    });

    if ('error' in result) return;

    setEdits(null);
    setStaged(null);
  };

  const handleDelete = async (id: number) => {
    setDeleting(null);
    resetDelete();
    const result = await deleteComparison(id);

    if ('error' in result || id !== savedId) return;

    showComparison();
  };

  return (
    <Box sx={pageSx}>
      <CompareHeader
        comparisons={comparisons}
        savedId={savedId}
        onLoad={showComparison}
        onNew={startNew}
        onDelete={setDeleting}
      />

      {studentWeights?.saved === false && <BeforeYouBegin onStart={scoreOnStudentWeights} />}

      <ComparisonControls
        colleges={colleges}
        picked={shown}
        onPickedChange={setStaged}
        isSaved={savedId !== undefined}
        canSave={canSave}
        saving={isCreating || isUpdating}
        saveFailed={createFailed}
        onSave={handleSave}
        onUpdate={handleUpdate}
        canCompare={canCompare}
        onCompare={handleCompare}
        canEditWeights={Boolean(comparison)}
        onEditWeights={() => setWeightsFor(null)}
        view={shownView}
        onViewChange={isPhone ? null : setView}
      />

      {(updateFailed || deleteFailed) && (
        <Typography id="compare-save-failed" variant="text-sm-medium" sx={errorSx}>
          {deleteFailed ? STRINGS.deleteFailed : STRINGS.saveFailed}
        </Typography>
      )}

      {picks.length === 0 && savedId === undefined && (
        <Typography id="compare-empty" variant="text-md-regular" sx={mutedSx}>
          {STRINGS.empty}
        </Typography>
      )}

      {!comparison && isLoading && <FullScreenLoading />}

      {isError && (
        <Typography variant="text-md-regular" sx={mutedSx}>
          {STRINGS.loadFailed}
        </Typography>
      )}

      {comparison && (
        <>
          {shownView === 'heatMap' ? (
            <CompareHeatMap comparison={comparison} onEditWeight={setWeightsFor} />
          ) : (
            <CompareCards comparison={comparison} />
          )}
          <FitLegend />
          {comparison.colleges.length > 0 && (
            <CompareAskLubav comparison={comparison} name={saved?.name ?? null} />
          )}
          {weightsFor !== undefined && (
            <CompareWeights
              initialFactor={weightsFor}
              onClose={() => setWeightsFor(undefined)}
              settings={comparison.settings}
              hasHome={comparison.has_home}
              onChange={handleSettingsChange}
            />
          )}
        </>
      )}

      {deleting && (
        <ConfirmModal
          open
          onClose={() => setDeleting(null)}
          headerText={STRINGS.deleteDialog.title}
          bodyText={STRINGS.deleteDialog.body}
          primaryAction={{
            id: 'compare-delete-confirm',
            label: STRINGS.deleteDialog.confirm,
            onClick: () => handleDelete(deleting.id),
          }}
          secondaryAction={{
            id: 'compare-delete-cancel',
            label: STRINGS.cancel,
            onClick: () => setDeleting(null),
          }}
        />
      )}
    </Box>
  );
};

// The weights dialogue saves the student's weights as it goes, so rescore after each Lubav turn.
const useRefetchAfterLubavTurn = () => {
  const dispatch = useAppDispatch();
  const isStreaming = useAppSelector(
    (state) => getSessionState(state, getActiveLubavSession(state)).coachLubav.streaming,
  );
  const wasStreaming = useRef(isStreaming);

  useEffect(() => {
    if (wasStreaming.current && !isStreaming) {
      dispatch(userCollegesApi.util.invalidateTags(['ComparisonDefaults']));
    }

    wasStreaming.current = isStreaming;
  }, [isStreaming, dispatch]);
};

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  gap: backrs.spacing['spacing-2xl'],
  paddingBottom: backrs.spacing['spacing-4xl'],
});

const errorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
